import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const columns = [
  { label: 'Company Name', key: 'company_name' },
  { label: 'PAN', key: 'pancard' },
  { label: 'GST Number', key: 'gst_number' },
  { label: 'Email', key: 'email' },
];

interface Company {
  id: number | string;
  company_name?: string;
  address?: string;
  pancard?: string;
  city?: string;
  state?: string;
  country?: string;
  gst_number?: string;
  gst_uin?: string;
  code?: string;
  cin?: string;
  email?: string;
  password?: string;
  [key: string]: any;
}

function decodeToken(token: any) {
  if (!token) return {};
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return {};
  }
}

const CompanyPage = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [deletingId, setDeletingId] = useState<number | string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [showPasswordId, setShowPasswordId] = useState<number | string | null>(null);
  const [selectedCompanyDetails, setSelectedCompanyDetails] = useState<Company | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [role, setRole] = useState<string | null>(null);

  // Subscription states
  const [plans, setPlans] = useState<any[]>([]);
  const [showSubModal, setShowSubModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [subForm, setSubForm] = useState({
    subscription_plan_id: '',
    subscription_start_date: '',
    subscription_status: 'active'
  });

  const navigate = useNavigate();

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/subscription-plans`);
      setPlans(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error loading plans:", err);
    }
  };

  // Fetch companies, and show only one if "role" in localStorage
  const fetchCompanies = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/auth/companies`);
      let companiesData: Company[] = Array.isArray(res.data) ? res.data : [];
      const token = localStorage.getItem('token');
      let decoded: any = {};
      if (token) {
        decoded = decodeToken(token);
      }
      let roleValue = decoded.role;
      setRole(roleValue); // So we can use it for conditional rendering

      // Verify actual user role from database
      const userId = decoded.userId || decoded.id;
      if (userId && roleValue !== 'company') {
        try {
          const ures = await axios.get(`${API_URL}/auth/allusersinfo`);
          const userFound = (Array.isArray(ures.data) ? ures.data : []).find((u: any) => String(u.id) === String(userId));
          if (userFound && userFound.role) {
            roleValue = userFound.role;
            setRole(roleValue);
          }
        } catch (e) {
          console.error("Error verifying role from database in Company.tsx:", e);
        }
      }

      if (roleValue === 'super-admin') {
        fetchPlans();
      }

      const activeWorkspaceId = localStorage.getItem("selected_company_id");
      if (roleValue === 'company') {
        // Only show company associated with token
        const companyId =
          decoded?.userId ||
          decoded?.id ||
          decoded?.companyId ||
          decoded?.company_id ||
          decoded?.cid;
        if (companyId) {
          companiesData = companiesData.filter(
            (company: Company) => String(company.id) === String(companyId)
          );
        } else {
          companiesData = [];
        }
      } else if (activeWorkspaceId && activeWorkspaceId !== "all") {
        companiesData = companiesData.filter(
          (company: Company) => String(company.id) === String(activeWorkspaceId)
        );
      }
      setCompanies(companiesData);
    } catch (err) {
      setError('Error loading companies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleAdd = () => {
    navigate('/addcompany/?company_id=new');
  };

  const handleEdit = (id: number | string) => {
    navigate(`/addcompany/?company_id=${id}`);
  };

  const handleDelete = async (id: number | string) => {
    setDeleteError('');
    const confirmed = window.confirm('Are you sure you want to delete this company? This will also delete all related bank details.');
    if (!confirmed) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API_URL}/auth/company/${id}`);
      setCompanies((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      setDeleteError('Error deleting company');
    } finally {
      setDeletingId(null);
    }
  };

  const displayedColumns = role === 'super-admin' ? [
    ...columns,
    { label: 'Plan Name', key: 'subscription_plan_name' },
    { label: 'Sub Status', key: 'subscription_status' }
  ] : columns;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Companies</h2>
        {/* Only show Add Company button if NOT in company role */}
        {(role !== 'company' && (
          <button
            onClick={handleAdd}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
          >
            Add Company
          </button>
        ))}
      </div>
      {error && <div className="text-red-600 mb-2">{error}</div>}
      {deleteError && <div className="text-red-600 mb-2">{deleteError}</div>}
      <div className="overflow-x-auto">
        <table className="min-w-[1200px] bg-white shadow rounded">
          <thead>
            <tr>
              {displayedColumns.map((col) => (
                <th
                  key={col.key}
                  className="py-2 px-4 border-b bg-gray-100 text-left text-sm font-medium"
                >
                  {col.label}
                </th>
              ))}
              <th className="py-2 px-4 border-b bg-gray-100 text-left text-sm font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={displayedColumns.length + 1} className="text-center py-6">Loading...</td>
              </tr>
            ) : companies.length === 0 ? (
              <tr>
                <td colSpan={displayedColumns.length + 1} className="text-center py-6">No companies found.</td>
              </tr>
            ) : (
              companies.map((company) => (
                <tr key={company.id} className="hover:bg-gray-50 transition">
                  {displayedColumns.map((col) =>
                    
                      <td key={col.key} className="py-2 px-4 border-b text-sm">
                        {col.key === 'subscription_status' ? (
                          company[col.key] === 'active' ? (
                            <span className="bg-green-100 text-green-800 font-bold px-2 py-0.5 rounded text-xs">Active</span>
                          ) : (
                            <span className="bg-red-100 text-red-800 font-bold px-2 py-0.5 rounded text-xs">{company[col.key] || 'Inactive'}</span>
                          )
                        ) : col.key === 'letterhead' ? (
                          company[col.key] ? (
                            <img
                              src={
                                String(company[col.key]).startsWith('http') || String(company[col.key]).startsWith('data:')
                                  ? company[col.key]
                                  : `${API_URL}${String(company[col.key]).startsWith('/') ? '' : '/'}${company[col.key]}`
                              }
                              alt="Letterhead"
                              className="h-10 w-auto rounded border border-gray-200"
                            />
                          ) : (
                            <span className="text-gray-400">-</span>
                          )
                        ) : (
                          company[col.key] || <span className="text-gray-400">-</span>
                        )}
                      </td>
                    
                  )}
                  <td className="py-2 px-4 border-b">
                    <button
                      className="px-3 py-1 mr-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition text-xs font-semibold"
                      onClick={() => {
                        setSelectedCompanyDetails(company);
                        setShowDetailsModal(true);
                      }}
                    >
                      More
                    </button>
                    <button
                      className="px-3 py-1 mr-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition text-xs"
                      onClick={() => handleEdit(company.id)}
                      disabled={deletingId === company.id}
                    >
                      Edit
                    </button>
                    <button
                      className={`px-3 py-1 mr-2 bg-red-600 text-white rounded hover:bg-red-700 transition text-xs ${deletingId === company.id ? 'opacity-60 cursor-not-allowed' : ''}`}
                      onClick={() => handleDelete(company.id)}
                      disabled={deletingId === company.id}
                    >
                      {deletingId === company.id ? 'Deleting...' : 'Delete'}
                    </button>
                    <button
                      className="px-3 py-1 mr-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition text-xs"
                      onClick={() => window.location.href = `/addBank?company_id=${company.id}`}
                    >
                      Bank Details
                    </button>
                    {role === 'super-admin' && (
                      <button
                        className="px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 transition text-xs"
                        onClick={() => {
                          setSelectedCompany(company);
                          setSubForm({
                            subscription_plan_id: company.subscription_plan_id ? String(company.subscription_plan_id) : '',
                            subscription_start_date: company.subscription_start_date ? company.subscription_start_date.substring(0, 10) : new Date().toISOString().substring(0, 10),
                            subscription_status: company.subscription_status || 'active'
                          });
                          setShowSubModal(true);
                        }}
                      >
                        Subscription
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Subscription Edit Modal */}
      {showSubModal && selectedCompany && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 animate__animated animate__fadeIn">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full border border-slate-200 shadow-xl relative">
            <button
              onClick={() => setShowSubModal(false)}
              className="absolute right-4 top-4 text-slate-400 hover:text-slate-600 text-lg font-bold"
              style={{ background: 'none', border: 'none' }}
            >
              &times;
            </button>
            <h3 className="font-extrabold text-lg text-indigo-950 mb-4">
              Manage Subscription: {selectedCompany.company_name}
            </h3>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setError('');
                try {
                  await axios.put(`${API_URL}/auth/company/${selectedCompany.id}/subscription`, subForm);
                  setShowSubModal(false);
                  fetchCompanies();
                } catch (err: any) {
                  setError(err.response?.data?.error || "Failed to update subscription.");
                }
              }}
              className="space-y-4"
            >
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Plan Tier</label>
                <select
                  value={subForm.subscription_plan_id}
                  onChange={(e) => setSubForm({ ...subForm, subscription_plan_id: e.target.value })}
                  className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-slate-800"
                >
                  <option value="">No Active Plan</option>
                  {plans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} (₹{p.price} / {p.duration_months}m)
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Start Date</label>
                <input
                  type="date"
                  value={subForm.subscription_start_date}
                  onChange={(e) => setSubForm({ ...subForm, subscription_start_date: e.target.value })}
                  className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-slate-800"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Status</label>
                <select
                  value={subForm.subscription_status}
                  onChange={(e) => setSubForm({ ...subForm, subscription_status: e.target.value })}
                  className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-slate-800"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="expired">Expired</option>
                </select>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowSubModal(false)}
                  className="px-4 py-2 border rounded-xl text-gray-500 bg-gray-50 hover:bg-gray-100 text-xs font-bold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-xs font-bold"
                >
                  Save Subscription
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedCompanyDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 animate__animated animate__fadeIn">
          <div className="bg-white rounded-3xl p-8 max-w-lg w-full border border-slate-200 shadow-2xl relative max-h-[85vh] overflow-y-auto text-left">
            <button
              onClick={() => setShowDetailsModal(false)}
              className="absolute right-6 top-6 text-slate-400 hover:text-slate-600 text-2xl font-bold"
              style={{ background: 'none', border: 'none' }}
            >
              &times;
            </button>
            <h3 className="font-extrabold text-xl text-indigo-950 mb-6 border-b pb-3 flex items-center gap-2">
              🏢 Company Profile Details
            </h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
              {[
                { label: 'Company Name', value: selectedCompanyDetails.company_name, full: true },
                { label: 'Address', value: selectedCompanyDetails.address, full: true },
                { label: 'PAN', value: selectedCompanyDetails.pancard },
                { label: 'Email', value: selectedCompanyDetails.email },
                { label: 'Phone / WhatsApp Number', value: selectedCompanyDetails.phone },
                { label: 'City', value: selectedCompanyDetails.city },
                { label: 'State', value: selectedCompanyDetails.state },
                { label: 'Country', value: selectedCompanyDetails.country },
                { label: 'GST Number', value: selectedCompanyDetails.gst_number },
                { label: 'GST UIN', value: selectedCompanyDetails.gst_uin },
                { label: 'Code', value: selectedCompanyDetails.code },
                { label: 'CIN', value: selectedCompanyDetails.cin },
                { label: 'Invoice Prefix', value: selectedCompanyDetails.prefix },
                { label: 'Invoice Postfix', value: selectedCompanyDetails.postfix },
                { label: 'Separator Symbol', value: selectedCompanyDetails.separatorsign },
                { label: 'WhatsApp API URL', value: selectedCompanyDetails.whatsapp_api_url, full: true },
                { label: 'WhatsApp API Token', value: selectedCompanyDetails.whatsapp_api_token, full: true },
              ].map((item, idx) => (
                <div key={idx} className={`${item.full ? 'col-span-2' : ''} border-b border-slate-100 pb-2`}>
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">{item.label}</label>
                  <span className="text-slate-800 font-semibold break-all">{item.value || <span className="text-slate-300 font-normal">-</span>}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-end pt-6">
              <button
                type="button"
                onClick={() => setShowDetailsModal(false)}
                className="px-6 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold uppercase tracking-wider transition shadow-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyPage;